"use client";

import { Compare } from "@/components/extern/compare";
import { useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";

const DEMOS = [
  {
    id: "portrait",
    before: "/images/demo/demo1_compare_before.png",
    after: "/images/demo/demo1_compare_after.png",
  },
  {
    id: "landscape",
    before: "/images/demo/demo2_compare_before.png",
    after: "/images/demo/demo2_compare_after.png",
  },
  {
    id: "old-photo",
    before: "/images/demo/demo3_compare_before.png",
    after: "/images/demo/demo3_compare_after.png",
  },
] as const;

export const ProductDemo = () => {
  const t = useTranslations('productDemo');
  const [selectedDemo, setSelectedDemo] = useState<(typeof DEMOS)[number]>(
    DEMOS[0]
  );
  const [isHovering, setIsHovering] = useState(false);

  return (
    <section
      id="how-it-works"
      className="w-full py-12 flex flex-col items-center justify-center gap-12 lg:gap-16"
    >
      <div className="w-full max-w-6xl text-start sm:text-center space-y-4 px-4">
        <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-neutral-100 via-neutral-300 to-neutral-500">
          {t('heading')}
        </h2>
        <p className="text-white/70 lg:text-center">
          {t('description')}
        </p>
      </div>

      <div
        className="w-full max-w-6xl mx-auto flex flex-col lg:flex-row items-center lg:items-start gap-4 lg:gap-5 
                       rounded-4xl border-4 border-neutral-700 p-4 bg-background"
      >
        {/* Increase width for comparison view */}
        <div className="w-full lg:w-4/5 relative">
          <div className="absolute -inset-1 bg-gradient-to-r from-rose-700/20 to-rose-900/20 rounded-[32px] blur-xl opacity-70 group-hover:opacity-100 transition duration-500" />
          <div
            className="relative bg-background/30 rounded-3xl overflow-hidden border border-white/10 shadow-xl backdrop-blur-sm"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            {/* Increase height by changing aspect ratio */}
            <Compare
              firstImage={selectedDemo.before}
              secondImage={selectedDemo.after}
              className="w-full aspect-[4/3] h-full overflow-hidden"
              firstImageClassName="object-cover"
              secondImageClassname="object-cover"
              autoplay={!isHovering}
              autoplayDuration={3000}
              showHandlebar={true}
              slideMode="drag"
            />

            <div className="absolute top-4 left-4 z-50">
              <div className="bg-background/70 backdrop-blur-md px-4 py-1.5 rounded-full shadow-[0_4px_12px_rgba(0,0,0,0.3)]">
                <span className="text-sm font-medium text-white text-center">
                  {t('labels.before')}
                </span>
              </div>
            </div>

            <div className="absolute top-4 right-4 z-50">
              <div className="bg-background/70 backdrop-blur-md px-4 py-1.5 rounded-full shadow-[0_4px_12px_rgba(0,0,0,0.3)]">
                <span className="text-sm font-medium text-white text-center">
                  {t('labels.after')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Demo Selection Cards Container - Decrease width */}
        <div className="w-full lg:w-1/5 flex flex-row lg:flex-col items-center lg:items-start justify-center lg:justify-start gap-4 lg:gap-5 mt-4 lg:mt-0">
          {DEMOS.map((demo) => {
            const demoKey = demo.id === 'old-photo' ? 'oldPhoto' : demo.id;
            return (
              <button
                key={demo.id}
                onClick={() => setSelectedDemo(demo)}
                className={`flex flex-col items-center gap-2 p-1 pb-2 rounded-3xl transition-all duration-200 border-2 w-full ${
                  selectedDemo.id === demo.id
                    ? "bg-white/100 border-white"
                    : "bg-[#1c1a1c] border-transparent hover:border-white/50"
                }`}
              >
                <div className="w-full h-24 relative rounded-2xl overflow-hidden flex-shrink-0">
                  <Image
                    src={demo.before}
                    alt={t(`demos.${demoKey}.title`)}
                    fill
                    className="transition-transform duration-300 group-hover:scale-105 object-cover"
                  />
                </div>
                <span
                  className={`text-sm font-medium text-center ${
                    selectedDemo.id === demo.id ? "text-background" : "text-white"
                  }`}
                >
                  {t(`demos.${demoKey}.title`)}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};
